/** @module csi/gallery/Splitter */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/dom-geometry",
	"dojo/dom-style",
	"dojo/NodeList",
	"dojo/query",
	"csi",
	"csi/_base/underscore",
	"csi/constants",
	"csi/gallery/_base",
	"csi/html",
	"csi/locale",
	"csi/printUtil",
	"csi/view/_Splitter"
], function(declare, domClass, domConstruct, domGeometry, domStyle, NodeList, query, csi, _, constants, csiGallery, html, locale, printUtil, _Splitter) {

	var CSS_FIT_ROWS = "csi-gallery-fit-rows";

	function _fitItemsPerRow(/*DOMNode*/eContainer, /*Number*/nItemsPerRow) {
		// summary: query for all galleryViewPlotRow divs, and make sure they do not overflow; if so, move
		// blocks around until they do fit
		var plotRows = _.toArray(query(".galleryViewPlotRow", eContainer));
		var plotRow;
		var bChanged;
		var i = 0;

		function _fixRowOverflow(plotRow) {
			// return true if right position of last child is greater than row width
			if (plotRow.lastChild == plotRow.firstChild) {
				return false;
			}

			// if item's right position fits within available row width, and number of children is under
			// given limit, then nothing to do
			const rowRect = plotRow.getBoundingClientRect();
			const lastBlockRect = plotRow.lastChild.getBoundingClientRect();

			if ((plotRow.childNodes.length <= nItemsPerRow) && (lastBlockRect.right <= rowRect.right)) {
				return false;
			}

			var jBlock = $(plotRow.lastChild);
			var jPlotRow = $(plotRow);

			// move item to next row - if none, then create one
			if (!plotRow.nextSibling) {
				jPlotRow.after($('<div/>', {"class": "galleryViewPlotRow"}));
				plotRows.push(plotRow.nextSibling);
			}

			jBlock.prependTo(plotRow.nextSibling);
			return true;
		}

		while (plotRow = plotRows[i++]) {
			while (_fixRowOverflow(plotRow)) {
				bChanged = true;
			}
		}

		return bChanged;
	}

	var Splitter = declare(_Splitter, {
		containerQuery: ".galleryViewContent",

		bImageAboveAttrs: false,
		bPlot: false,
		bResizeAttributesToImage: false,
		galleryRowsPerPage: 0,
		keysToAlign: null,
		nActualRows: 0,
		plotNode: null,

		_bGroupHeaders: null,
		_bPageHeaders: null,

		/**
		 * Split view content so it fits nicely on pages with given width and height
		 * @param {DOMNode[]} aPages
		 * @param {DOMNode} viewRoot
		 * @param {number} nPageHeight
		 * @param {number} nPageWidth
		 * @param {Function} [copyCallback]
		 */
		split: function(aPages, viewRoot, nPageHeight, nPageWidth, copyCallback) {
			this.bImageAboveAttrs = html.getDataAttribute(viewRoot, html.DATA_GALLERY_IMAGE_ON_TOP);
			this.bResizeAttributesToImage = html.getDataAttribute(viewRoot, html.DATA_GALLERY_RESIZE_ATTRIBUTES);
			this.galleryRowsPerPage = html.getDataAttribute(viewRoot, html.DATA_GALLERY_ROWS_PER_PAGE);

			var keysToAlign = html.getDataAttribute(viewRoot, html.DATA_GALLERY_ALIGN_KEYS);
			if (keysToAlign) {
				this.keysToAlign = keysToAlign.split(",");
			}

			var plotNode = this.plotNode = query(".csi-gallery-table", viewRoot)[0];
			if (plotNode) {
				var groupHeaderStyle = html.getDataAttribute(plotNode, html.DATA_GALLERY_GROUP_STYLE);

				this.bPlot = true;
				this._bGroupHeaders = (groupHeaderStyle == constants.GALLERY_HEADERS_PER_GROUP);
				this._bPageHeaders = (groupHeaderStyle == constants.GALLERY_HEADERS_PER_PAGE);

				// if plotting group headers then remove row/column headers and show group headers; otherwise remove group headers
				if (this._bGroupHeaders) {
					query("> thead, > tbody > tr > th", plotNode).orphan();
					query(".galleryViewPlotHeader", plotNode).style("display", "");
				} else {
					query(".galleryViewPlotHeader", plotNode).orphan();
				}

				this._splitPlot(aPages, nPageHeight, nPageWidth, viewRoot, copyCallback);
			} else {
				this.inherited(arguments);
			}
		},

		/**
		 * Return table body rows
		 * @param {DOMNode} eContainer - body of table
		 * @return {NodeList}
		 */
		_getRefNodes: function(eContainer) {
			return csiGallery.getBlocks(eContainer);
		},

		/**
		 * Resize view so blocks align along rows
		 * @param {DOMNode} rootNode
		 */
		_resizeBlocks: function(rootNode) {
			this.nActualRows = csiGallery.resizeBlocks(rootNode, this.keysToAlign, this.bPlot, this.bResizeAttributesToImage);
		},

		_preSplit: function(/*DOMNode*/eContainer, /*DOMNode[]*/refNodes, /*Number*/nMaxPageHeight) {
			// summary: if specifying number of rows per print page, then adjust heights of images to fit fully on subsequent pages; otherwise, resize
			// to make sure everything aligned
			if (this.galleryRowsPerPage) {
				this._fitRowsPerPage(eContainer, refNodes, 0, nMaxPageHeight);
			}

			// make sure resize is called at least once - it is not guaranteed to be called from _fitRowsPerPage
			if (!this.nActualRows) {
				this._resizeBlocks(eContainer);
			}
		},

		_splitPlot: function(/*DOMNode[]*/aPages, /*Number*/nPageHeight, /*Number*/nPageWidth, /*DOMNode*/viewRoot, /*Function?*/copyCallback) {
			// summary: split plot view into multiple pages if necessary
			var eContainer = this._getContainerNode(viewRoot);
			var eView = aPages.last();
			var table = this.plotNode;
			var tbody = table.lastChild;
			var thead = !this._bGroupHeaders && table.firstChild;
			var nRows = tbody.childNodes.length;
			if (!nRows) return;

			// for composite views, need to make sure we are working with available width rather than full page width;
			// add 6 more pixels to account for border and padding for composite view
			var bOwnPage = html.getDataAttribute(viewRoot, html.PRINT_BREAK);
			var borderOverhead = 6;
			var firstPageHeightOverhead = 0;
			var otherPageHeightOverhead = 0;

			var pageNode = html.getParentByClassName(table, html.CSS_PAGE);
			if (pageNode) {
				var mbPage = domGeometry.getMarginBox(pageNode);
				var cbTable = domGeometry.getContentBox(table);
				nPageWidth -= (mbPage.w - cbTable.w) + borderOverhead;

				html.setVisible(table, false);

				// if gallery is guaranteed to be on own page, then all pages have same overhead; otherwise,
				// need to remove content from previous views to determine overhead of all other pages
				firstPageHeightOverhead = eView.offsetHeight + borderOverhead;
				otherPageHeightOverhead = bOwnPage ? firstPageHeightOverhead : (this._getOtherPageHeightOverhead(eView, copyCallback) + borderOverhead);

				html.setVisible(table, true);
			}

			// determine max column width - use first body row to calculate since header row is optional
			var nlContent = csiGallery.getGroupContentNodes(tbody.firstChild);
			var nColumns = nlContent.length + (thead ? 1 : 0);
			var maxWidth = 0;
			var tdMax;

			nlContent.forEach(function(domNode) {
				var w = domStyle.get(domNode, "width");
				if (w > maxWidth) {
					maxWidth = Math.ceil(w);
					tdMax = domNode.parentNode;
				}
			}, this);

			// fix plot headers to make sure they do not change across pages - if header width was specified in
			// custom view, then it will already be applied
			var groupHeaderWidth = html.getDataAttribute(table, html.DATA_GALLERY_GROUP_WIDTH);
			if (thead && !groupHeaderWidth) {
				csiGallery.fixGroupHeaders(thead);
			}

			// determine number of columns per page - for first page, add 1 extra pixel for border between header and cell
			var mbHeader = thead && domGeometry.getMarginBox(thead.firstChild.firstChild);
			var mb = domGeometry.getMarginBox(tdMax);
			var widthDiff = mb.w - maxWidth;
			var nFirstPageWidth = nPageWidth - ((mbHeader && mbHeader.w + 1) || 0);
			var nColumnsPerPage = Math.max(Math.floor(nFirstPageWidth / mb.w), 1);

			// make all column widths consistent
			var maxWidthFirstPage = (Math.floor(nFirstPageWidth / nColumnsPerPage) - widthDiff) + "px";

			// if printing headers for each page or each group, then column width of other pages is same as first
			if (this._bPageHeaders || this._bGroupHeaders) {
				maxWidth = maxWidthFirstPage;
			} else {
				maxWidth = (Math.floor(nPageWidth / nColumnsPerPage) - widthDiff) + "px";
			}

			// now get all rows and fix width of content within table cells
			nlContent = csiGallery.getGroupContentNodes(tbody);
			nlContent.forEach(function(domNode) {
				var column = csiGallery.getGroupColumn(domNode);
				domStyle.set(domNode, "width", (column < nColumnsPerPage) ? maxWidthFirstPage : maxWidth);
			});

			// make sure blocks have consistent heights after increasing width of columns to max
			this._resizeBlocks(eContainer);

			var groupItemsPerRow = html.getDataAttribute(table, html.DATA_GALLERY_GROUP_ITEMS_PER_ROW);
			if (!groupItemsPerRow) {
				// if did not specify number of items per group per row, then wrap blocks in row with div to match DOM structure when number is specified
				this._groupItemsPerRow(eContainer);
			} else if (_fitItemsPerRow(eContainer, groupItemsPerRow)) {
				// make sure number of items in row does not overflow available width; if items were moved,
				// then need to resize so that image/attribute heights again match
				this._resizeBlocks(eContainer);
			}

			// unwrap children of galleryViewPlotContent, making sure to keep width set earlier
			csiGallery.getGroupContentNodes(tbody).forEach(function(domNode) {
				var pn = domNode.parentNode;

				if (this._bGroupHeaders) {
					// force group header to be its current width
					domStyle.set(pn.firstChild, "width", domStyle.get(pn.firstChild, "width") + "px");
				} else {
					// if no group headers, then create them just to make sure width is preserved
					domConstruct.create("div", {
						"class": "galleryViewPlotHeader",
						style: "margin: 0; padding: 0; width:" + domStyle.get(domNode, "width") + "px"
					}, pn, "first");
				}

				html.unwrap(domNode);
			}, this);

			// done if no content
			var nItemRows = this.nActualRows;
			if (!nItemRows) return;

			// capture height overhead per table row including header
			var tdSample = tbody.firstChild.lastChild;
			var headerHeightOverhead = (this._bGroupHeaders ? 0 : mbHeader.h);
			var rowHeightOverhead = domGeometry.getMarginBox(tdSample).h - domGeometry.getContentBox(tdSample).h + domGeometry.getMarginBox(tdSample.firstChild).h + 1;
			var refNodes = this._getRefNodes(eContainer);

			// if specifying number of rows per print page, then adjust heights of images to fit on page; available height is calculated based on page
			// height minus header height minus the height overhead for each new group row (assume worst case that each row is new group)
			if (this.galleryRowsPerPage) {
				console.assert(firstPageHeightOverhead == otherPageHeightOverhead);
				this._fitRowsPerPage(eContainer, refNodes, rowHeightOverhead, nPageHeight - firstPageHeightOverhead - headerHeightOverhead);
			}

			// record item row information
			this._buildRowMaps(refNodes);

			// determine what rows go on each page
			var pageRows = [];
			var h = firstPageHeightOverhead + headerHeightOverhead + rowHeightOverhead;
			var r1 = 0;
			var ir1 = 0;
			var hNext, r2, ir2;

			while (r1 < nRows) {
				r2 = r1;
				ir2 = ir1;

				while (r2 < nRows) {
					if ((ir2 == nItemRows) || (this.rowMap[ir2] > r2 + 1)) {
						// could get here if all item rows are processed but still table rows to process, or
						// item rows skip at least one table row
						hNext = rowHeightOverhead;

						if (h + hNext > nPageHeight) {
							break;
						}

						// go to next row
						h += hNext;
						++r2;
					} else {
						// add item row height
						hNext = this.rowHeightMap[ir2];

						// if first item or if item moves to new table row, then add overhead for row
						if (this.rowMap[ir2] != r2) {
							hNext += rowHeightOverhead;
						}

						// break if height will be exceeded by this row, but always make sure at least one row fits
						if ((ir1 != ir2) && (h + hNext > nPageHeight)) {
							break;
						}

						// go to next item row
						h += hNext;
						r2 = this.rowMap[ir2];
						++ir2;

						// if finished with items, then see if finished with rows too
						if ((ir2 == nItemRows) && (r2 == nRows - 1)) {
							++r2;
						}
					}
				}

				// copy part of table to new page
				pageRows.push([r1, Math.min(r2, nRows - 1), ir1, ir2 - 1]);

				// if finished with one page and next item is on different table row, then advance to that table row
				if (this.rowMap[ir2] > r2) {
					++r2;
				}

				// calculate rows for next page
				h = otherPageHeightOverhead + rowHeightOverhead + (this._bPageHeaders ? mbHeader.h : 0);
				r1 = r2;
				ir1 = ir2;
			}

			// reverse direction so can move DOM elements to pages without cloning
			pageRows.reverse();

			// remove table from container so that it is not contained in page copy below
			// and so each change to DOM does not force repaint
			eContainer.removeChild(table);

			// split table into table per page
			var tables = [];
			var c1 = 0;
			var c2;

			while (c1 < nColumns) {
				// if first column and there are headers, then increment column by one more
				c2 = c1 + nColumnsPerPage + ((thead && !c1) ? 1 : 0);
				c2 = Math.min(c2, nColumns);

				tables = tables.concat(_.map(pageRows, function(pr) {
					return this._copyTable(c1, c2 - 1, pr[0], pr[1], pr[2], pr[3]);
				}, this).reverse());

				c1 = c2;
			}

			// create new pages for all but first table
			var containers = _.map(tables, function(t, i) {
				if (i) {
					var result = this._copyPageWithContainer(aPages, eView, eContainer, false, copyCallback);
					eView = result.viewNode;
					eContainer = result.containerNode;
				}

				return eContainer;
			}, this);

			// finally, add tables to corresponding container - must do so last so that copying the view
			// does not also copy the table
			_.each(containers, function(e, i) {
				e.appendChild(tables[i]);
			});
		},

		/**
		 * @override
		 * build various lookup maps for item rows
		 */
		_buildRowMaps(refNodes, nPageHeight, otherPageHeightOverhead, firstPageHeightOverhead, nPageWidth) {
			var heightMap = this.rowHeightMap = {};
			var rowMap = this.rowMap = {};
			var bPlot = this.bPlot;

			this.nRows = this.nActualRows;

			_.each(refNodes, function(refNode, i) {
				var r = csiGallery.getBlockRow(refNode);

				if (!heightMap[r]) {
					heightMap[r] = domGeometry.getMarginBox(refNode).h;
					rowMap[r] = bPlot ? html.getElementIndex(html.getParentByTagName(refNode, "tr")) : i;
				}
			});

			if (bPlot) {
				// map item rows to child index in plot row
				var indexMap = this.rowIndexMap = {};
				var index;

				for (var i = 0; i < this.nRows; ++i) {
					if (i && rowMap[i] == rowMap[i - 1]) {
						++index;
					} else {
						index = 0;
					}

					indexMap[i] = index;
				}
			}
		},

		_copyTable: function(/*Number*/startColumn,
							 /*Number*/endColumn,
							 /*Number*/startRow,
							 /*Number*/endRow,
							 /*Number*/startItemRow,
							 /*Number*/endItemRow) {
			// summary: copy portion of table as child of contentNode
			var table = this.plotNode.cloneNode(false);
			var sourceHead = !this._bGroupHeaders && this.plotNode.firstChild;
			var sourceBody = this.plotNode.lastChild;

			var bColumnHeaders = this._bPageHeaders || (!this._bGroupHeaders && !startItemRow);
			var bRowHeaders = this._bPageHeaders || (!this._bGroupHeaders && !startColumn);

			var i, c, r, divHeader, sourceItem, sourceTr, sourceTd, thead, tr, td;

			var _copy = function(node, parent, deep) {
				var n = node.cloneNode(deep || false);
				parent.appendChild(n);
				return n;
			};

			// if printing row headers, advance start column by 1 if on first column since that will be treated specially
			if (bRowHeaders && !startColumn) {
				startColumn = 1;
			}

			// copy header row if necessary
			if (bColumnHeaders) {
				thead = _copy(sourceHead, table);
				sourceTr = sourceHead.firstChild;
				tr = _copy(sourceTr, thead);

				if (bRowHeaders) {
					_copy(sourceTr.childNodes[0], tr, true);
				}

				for (c = startColumn; c <= endColumn; ++c) {
					_copy(sourceTr.childNodes[c], tr, true);
				}
			}

			var tbody = _copy(sourceBody, table);

			for (r = startRow; r <= endRow; ++r) {
				sourceTr = sourceBody.childNodes[r];
				tr = _copy(sourceTr, tbody);

				if (bRowHeaders) {
					// deep copy header cells
					_copy(sourceTr.childNodes[0], tr, true);
				}

				for (c = startColumn; c <= endColumn; ++c) {
					sourceTd = sourceTr.childNodes[c];

					// shallow copy group cells
					td = _copy(sourceTd, tr);

					// copy items in cell that match current row
					for (i = endItemRow; i >= startItemRow; --i) {
						if (this.rowMap[i] == r) {
							// index is always offset by 1 to include header - even when no group headers because
							// empty headers are still created to preserve cell width
							if (sourceItem = sourceTd.childNodes[this.rowIndexMap[i] + 1]) {
								domConstruct.place(sourceItem, td, "first");
							} else {
								domConstruct.create("div", {
									style: "height: " + this.rowHeightMap[i] + "px"
								}, td, "first");
							}
						}
					}

					// making assumption that (group) header is always consistent height
					divHeader = sourceTd.firstChild.cloneNode(true);

					if (this._bGroupHeaders && (this.rowMap[startItemRow] == r) && (this.rowIndexMap[startItemRow] > 0)) {
						html.create("span", {
							"class": "dijitReset dijitInline",
							style: "margin-left: 5px",
							innerText: "(" + locale.resource("Continued") + ")"
						}, divHeader);
					}

					domConstruct.place(divHeader, td, "first");
				}
			}

			return table;
		},

		_fitRowsPerPage: function(/*DOMNode*/domRoot, /*DOMNode[]*/refNodes, /*Number*/nRowHeightOverhead, /*Number*/nPageHeight) {
			// summary: adjust image height to best fit on the page given the number of rows; if can't fit given number
			// of rows, they try to fit with less rows
			domClass.add(domRoot, CSS_FIT_ROWS);

			var attrHeight = _.reduce(refNodes, function(h, refNode) {
				// NOTE: assumption here is that csiGalleryProperties is last child
				return Math.max(h, domStyle.get(refNode.lastChild, "height"));
			}, 0);

			domClass.remove(domRoot, CSS_FIT_ROWS);

			var rowPadding = domGeometry.getMarginBox(refNodes[0]).h - domGeometry.getContentBox(refNodes[0]).h;
			var rowHeight, imageHeight;
			var nRows = this.galleryRowsPerPage;

			while (nRows) {
				rowHeight = Math.floor((nPageHeight - nRows * nRowHeightOverhead) / nRows);
				imageHeight = rowHeight - rowPadding - (this.bImageAboveAttrs ? attrHeight : 0);

				if ((rowHeight > attrHeight) && (imageHeight >= 16)) {
					// found appropriate height for image, so update image height
					query("div.csiGalleryProperties", domRoot).style("height", (this.bImageAboveAttrs ? attrHeight : imageHeight) + "px");
					query("div.csiGalleryImage", domRoot).style("height", imageHeight + "px");
					query("div.csiGalleryImage img", domRoot).style({
						"max-height": "100%",
						"max-width": "100%"
					});

					// make sure blocks have consistent heights
					this._resizeBlocks(domRoot);
					return;
				}

				--nRows;
			}
		},

		_groupItemsPerRow: function(/*DOMNode*/eContainer) {
			// summary: group items per row per cell
			var refNodes = this._getRefNodes(eContainer);
			var refNodesByRow = csi.hashArray(refNodes, csiGallery.getBlockRow, null, null, true);
			var refNodesByCell;
			var a = [];
			var pn;

			function getRefNodesByCell(domNode) {
				if (pn != domNode.parentNode) {
					pn = domNode.parentNode;
					a.push(refNodesByCell = []);
				}

				refNodesByCell.push(domNode);
			}

			// separate row blocks per cell
			for (var i = 0; i < this.nActualRows; ++i) {
				pn = null;
				_.each(refNodesByRow[i], getRefNodesByCell);
			}

			// wrap row block in cells
			_.each(a, function(refNodes) {
				new NodeList(refNodes).wrapAll('<div class="galleryViewPlotRow"></div>');
			});
		}
	});

	printUtil.registerViewSplitter("gallery", Splitter);

	return Splitter;
});